import React from 'react';
import { AppBar, Box, Button, Container, Toolbar, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CalculateIcon from '@mui/icons-material/Calculate';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useDemo } from '../../../contexts/DemoContext';
import { AMBER } from '../demoContent';

interface DemoTopBarProps {
  onStartTour: () => void;
  loading: boolean;
}

/**
 * The demo site's slim chrome: wordmark on the left, one way in on the right. Replaces the app's
 * MainLayout header, whose login/register navigation has no business on a no-password demo.
 *
 * When a returning visitor has a sandbox, PersonaBar (52px, zIndex drawer + 2) sticks above
 * everything — this bar then sticks 52px down and below it in stacking order, never fighting it.
 */
export default function DemoTopBar({ onStartTour, loading }: DemoTopBarProps) {
  const theme = useTheme();
  const { enabled, session } = useDemo();
  const personaBarVisible = enabled && Boolean(session);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        top: personaBarVisible ? 52 : 0,
        zIndex: theme.zIndex.appBar,
        bgcolor: alpha(theme.palette.background.paper, 0.92),
        backdropFilter: 'blur(8px)',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="lg" disableGutters>
        <Toolbar sx={{ minHeight: { xs: 56, md: 60 }, px: { xs: 2, lg: 3 }, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <CalculateIcon sx={{ color: 'primary.main', fontSize: 26 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: 'primary.main', letterSpacing: '-0.01em' }}>
              MathWiz{' '}
              <Box component="span" sx={{ fontWeight: 400, color: 'text.primary' }}>
                League
              </Box>
            </Typography>
          </Box>

          <Button
            size="small"
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={onStartTour}
            disabled={loading}
            sx={{
              bgcolor: AMBER.main,
              color: AMBER.contrastText,
              fontWeight: 600,
              px: 2.5,
              '&:hover': { bgcolor: AMBER.dark },
              '&.Mui-disabled': {
                bgcolor: alpha(AMBER.main, 0.4),
                color: alpha(AMBER.contrastText, 0.5),
              },
            }}
          >
            Start the tour
          </Button>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
